import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faSearch, faSync, faStore,
    faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * SuperAdminEmpresas — Read-only view of all companies with branch counts.
 */
const SuperAdminEmpresas = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [search, setSearch]       = useState('');
    const [expanded, setExpanded]   = useState(null);

    useEffect(() => { fetchCompanies(); }, []);

    const fetchCompanies = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/companies', {}, r => {
            if (r.success) setCompanies(r.data || []);
            else Notify({ type:'fail', text:'Error al cargar empresas' });
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return companies.filter(c =>
            !q ||
            c.EmpresaNombre?.toLowerCase().includes(q) ||
            c.AdminEmail?.toLowerCase().includes(q)
        );
    }, [companies, search]);

    const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

    return (
        <div className="animate-in pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ fontWeight:800, margin:0 }}>
                        <FontAwesomeIcon icon={faBuilding} style={{ color:'var(--sa-primary)', marginRight:'0.6rem' }} />
                        Empresas
                    </h2>
                    <p className="text-muted small mt-1 mb-0">Vista global de todas las empresas registradas en la plataforma</p>
                </div>
                <button className="sa-btn sa-btn-ghost" onClick={fetchCompanies} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> Actualizar
                </button>
            </div>

            <div className="sa-search-bar mb-4" style={{ maxWidth:'100%' }}>
                <FontAwesomeIcon icon={faSearch} style={{ color:'#475569' }} />
                <input
                    placeholder="Buscar empresa o email del admin..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {search && (
                    <span style={{ color:'#475569', fontSize:'0.75rem', whiteSpace:'nowrap' }}>
                        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" style={{ color:'var(--sa-primary)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="sa-empty">
                        <FontAwesomeIcon icon={faBuilding} style={{ fontSize:'3rem', opacity:0.15, display:'block', margin:'0 auto 1rem' }} />
                        <p>No se encontraron empresas</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Empresa</th>
                                    <th>Administrador</th>
                                    <th>Plan</th>
                                    <th>Sucursales</th>
                                    <th>Tipos de Turno</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c, idx) => (
                                    <React.Fragment key={c.EmpresaId}>
                                        <tr style={{ cursor:'pointer' }} onClick={() => toggleExpand(c.EmpresaId)}>
                                            <td style={{ color:'#475569', fontSize:'0.78rem' }}>{idx + 1}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{
                                                        width:36, height:36, borderRadius:10,
                                                        background:'rgba(168,85,247,0.1)',
                                                        display:'flex', alignItems:'center', justifyContent:'center',
                                                        color:'var(--sa-primary)', flexShrink:0
                                                    }}>
                                                        <FontAwesomeIcon icon={faBuilding} />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight:700, fontSize:'0.9rem' }}>{c.EmpresaNombre}</div>
                                                        {c.EmpresaRazonSocial && (
                                                            <small style={{ color:'#475569' }}>{c.EmpresaRazonSocial}</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontSize:'0.85rem' }}>
                                                    <div>{c.AdminNombre} {c.AdminApellido}</div>
                                                    <small style={{ color:'#64748b' }}>{c.AdminEmail}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`sa-badge ${
                                                    c.PlanId === 3 ? 'sa-badge-business' :
                                                    c.PlanId === 2 ? 'sa-badge-premium'  : 'sa-badge-trial'
                                                }`}>
                                                    {c.PlanNombre || 'Trial'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon icon={faStore} style={{ color:'#3b82f6', fontSize:'0.8rem' }} />
                                                    <span style={{ fontWeight:700 }}>{c.TotalSucursales ?? 0}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight:700 }}>{c.TotalTipos ?? 0}</span>
                                            </td>
                                            <td style={{ textAlign:'right' }}>
                                                <FontAwesomeIcon
                                                    icon={expanded === c.EmpresaId ? faChevronUp : faChevronDown}
                                                    style={{ color:'#475569', fontSize:'0.8rem' }}
                                                />
                                            </td>
                                        </tr>

                                        {/* Expanded: branches list */}
                                        {expanded === c.EmpresaId && (
                                            <tr>
                                                <td colSpan={7} style={{ padding:0 }}>
                                                    <div style={{
                                                        background:'rgba(168,85,247,0.03)',
                                                        borderTop:'1px solid rgba(168,85,247,0.08)',
                                                        padding:'1rem 2rem'
                                                    }}>
                                                        {(c.branches && c.branches.length > 0) ? (
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {c.branches.map(b => (
                                                                    <div key={b.SucursalId} style={{
                                                                        background:'rgba(255,255,255,0.03)',
                                                                        border:'1px solid rgba(255,255,255,0.06)',
                                                                        borderRadius:8, padding:'6px 14px',
                                                                        fontSize:'0.8rem', color:'#94a3b8',
                                                                        display:'flex', alignItems:'center', gap:6
                                                                    }}>
                                                                        <FontAwesomeIcon icon={faStore} style={{ color:'#3b82f6', fontSize:'0.7rem' }} />
                                                                        {b.SucursalNombre}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p style={{ color:'#475569', fontSize:'0.82rem', margin:0 }}>
                                                                Sin sucursales registradas
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdminEmpresas;
